import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { ShieldCheck, Lock, BadgeCheck, ArrowRight } from "lucide-react";

/**
 * EscrowTrustBanner — high-impact promotional strip on the homepage that
 * communicates the CARTAL Escrow guarantee.  Designed to sit between the Hero
 * and the TrustStrip and acts as a primary CTA to /trust (the Trust & Safety
 * Center which explains escrow in detail).
 *
 * Layout:
 *  - Dark ink background with subtle gold radial glow
 *  - Three feature pills (escrow / verified parties / dispute support)
 *  - Headline + sub-line in Arabic/English with strong CTA button
 *  - Fully responsive: stacks vertically below md
 */
export default function EscrowTrustBanner() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  const pills = [
    {
      icon: Lock,
      ar: "أموالك في ضمان حتى الاستلام",
      en: "Funds held until you receive the vehicle",
    },
    {
      icon: BadgeCheck,
      ar: "بائعون موثّقون بالهوية والسجل",
      en: "Sellers verified by national ID & CR",
    },
    {
      icon: ShieldCheck,
      ar: "دعم متخصص لفض النزاعات",
      en: "Dedicated dispute mediation team",
    },
  ];

  return (
    <section
      className="relative overflow-hidden bg-[#0a0a0a] text-[#f1ecdf]"
      data-testid="escrow-trust-banner"
      aria-label="CARTAL Escrow guarantee"
    >
      {/* Decorative gold glow */}
      <div
        aria-hidden
        className="absolute -top-32 -end-32 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(201,165,92,0.35) 0%, rgba(201,165,92,0) 65%)",
          filter: "blur(8px)",
        }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -start-40 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(184,137,59,0.22) 0%, rgba(184,137,59,0) 65%)",
          filter: "blur(8px)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-12 md:py-14">
        <div className="grid md:grid-cols-[1.25fr_1fr] gap-10 md:gap-12 items-center">
          {/* Headline column */}
          <div>
            <div
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase font-semibold mb-4"
              style={{ color: "#d4ae6a" }}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              {isAr ? "ضمان كارتال" : "CARTAL Escrow"}
            </div>

            <h2
              className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05] font-medium"
              style={{ color: "#ffffff" }}
            >
              {isAr ? (
                <>
                  أموالك آمنة.
                  <br />
                  <span style={{ color: "#d4ae6a" }}>صفقتك مضمونة.</span>
                </>
              ) : (
                <>
                  Your money is safe.
                  <br />
                  <span style={{ color: "#d4ae6a" }}>Your deal is guaranteed.</span>
                </>
              )}
            </h2>

            <p
              className="mt-5 text-base sm:text-lg leading-relaxed max-w-xl"
              style={{ color: "#e8e1cf" }}
            >
              {isAr
                ? "ندفع للبائع فقط بعد ما تستلم المركبة وتؤكد رضاك. خدمة الضمان متاحة على كل صفقة في كارتال — بدون رسوم خفية."
                : "We only release payment to the seller after you've received the vehicle and confirmed satisfaction. Available on every CARTAL deal — zero hidden fees."}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/trust"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-colors"
                style={{ backgroundColor: "#c9a55c", color: "#0a0a0a" }}
                data-testid="escrow-banner-cta-learn"
              >
                {isAr ? "كيف يعمل الضمان؟" : "How escrow works"}
                <ArrowRight className="w-4 h-4 rtl:rotate-180" />
              </Link>
              <Link
                to="/marketplace"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors border-2"
                style={{
                  borderColor: "rgba(212,174,106,0.55)",
                  color: "#f1ecdf",
                  backgroundColor: "transparent",
                }}
                data-testid="escrow-banner-cta-browse"
              >
                {isAr ? "تصفّح مركبات مضمونة" : "Browse protected listings"}
              </Link>
            </div>
          </div>

          {/* Feature pills column */}
          <ul
            className="space-y-3"
            aria-label={isAr ? "مزايا ضمان كارتال" : "CARTAL Escrow benefits"}
          >
            {pills.map((p, i) => {
              const Icon = p.icon;
              return (
                <li
                  key={i}
                  className="flex items-start gap-4 rounded-2xl p-4 sm:p-5"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(201,165,92,0.22)",
                    backdropFilter: "blur(6px)",
                  }}
                  data-testid={`escrow-banner-pill-${i}`}
                >
                  <span
                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      backgroundColor: "rgba(201,165,92,0.15)",
                      border: "1px solid rgba(201,165,92,0.45)",
                    }}
                  >
                    <Icon className="w-5 h-5" style={{ color: "#d4ae6a" }} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-sm sm:text-base font-semibold leading-snug"
                      style={{ color: "#ffffff" }}
                    >
                      {isAr ? p.ar : p.en}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
